/**
 * Reusable primitive for shareable, URL-backed filter/search state.
 *
 * A page supplies a `UrlStateCodec` (its state <-> query string mapping); this
 * hook snapshots the mount-time query string, decodes it once as `initialState`,
 * and returns `writeToUrl`/`clearUrl` that `replace` the query string. It does
 * NOT decide WHEN to write — that is page policy. No UI; a hook, not a component.
 */
import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router';

/**
 * Bidirectional mapping between a feature's state `T` and a query string. Each
 * feature owns its codec because the field-by-field encoding, and especially the
 * VALIDATION on decode, are feature-specific — a hand-edited or stale link must
 * only ever decode into a state the feature could itself produce.
 */
export interface UrlStateCodec<T> {
  /** Serializes state into query params for a shareable link. */
  readonly encode: (value: T) => URLSearchParams;
  /** Parses query params back into validated state. */
  readonly decode: (params: URLSearchParams) => T;
}

export interface UrlSharedState<T> {
  /**
   * State decoded from the query string present on FIRST mount. Stable for the
   * lifetime of the component — seed the page's own `useState` with it.
   */
  readonly initialState: T;
  /** Replaces the query string with the encoding of `value` (a shareable link). */
  readonly writeToUrl: (value: T) => void;
  /** Clears the query string back to a bare path. */
  readonly clearUrl: () => void;
}

/**
 * Wires a feature's state to the URL for sharing. See the module docs for the
 * division of responsibility between this hook (the reusable mechanics) and the
 * caller (the codec, and the policy of when to write).
 */
export function useUrlSharedState<T>(
  codec: UrlStateCodec<T>,
): UrlSharedState<T> {
  const [searchParams, setSearchParams] = useSearchParams();

  // Snapshot the params from first mount so our own writes don't re-hydrate.
  const initialParamsRef = useRef(searchParams);
  // Codec in a ref so write/clear keep a stable identity even if it's rebuilt.
  const codecRef = useRef(codec);
  codecRef.current = codec;

  const initialState = useMemo(
    () => codecRef.current.decode(initialParamsRef.current),
    [],
  );

  const writeToUrl = useCallback(
    (value: T) => {
      setSearchParams(codecRef.current.encode(value), { replace: true });
    },
    [setSearchParams],
  );

  const clearUrl = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  return { initialState, writeToUrl, clearUrl };
}
